import { useState } from "react";
import { Wheat, Droplet, Check, ShoppingBag, Sparkles, Stethoscope } from "lucide-react";
import { HorseArt } from "@/components/game/HorseArt";

const Row = ({ label, value, done }) => (
  <div className="flex items-center justify-between py-0.5 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className={`flex items-center gap-1 font-semibold ${done ? "text-green-600" : "text-foreground/60"}`}>
      {done && <Check size={13} />}{value}
    </span>
  </div>
);

const Picker = ({ testid, label, items, inventory, onGive, disabled }) => {
  const owned = (items || []).filter((it) => (inventory?.[it.id] || 0) > 0);
  const [sel, setSel] = useState("");
  if (owned.length === 0) return null;
  return (
    <div className="flex items-center gap-2">
      <select data-testid={`${testid}-select`} value={sel} onChange={(e) => setSel(e.target.value)}
        className="h-10 flex-1 rounded-lg border border-border bg-background px-2 text-sm">
        <option value="">{label}…</option>
        {owned.map((it) => <option key={it.id} value={it.id}>{it.name} ({inventory[it.id]})</option>)}
      </select>
      <button data-testid={`${testid}-give`} disabled={disabled || !sel} onClick={() => { onGive(sel); setSel(""); }}
        className="h-10 rounded-lg bg-secondary px-3 text-sm font-bold text-white disabled:opacity-40 active:scale-95">
        Give
      </button>
    </div>
  );
};

export const DailyCare = ({ save, shop, inventory, onCare, onOpenShop, busy }) => {
  const care = save.care || {};
  const groom = !!save.groom_active;
  const done = (care.hay && care.water) || groom;
  const nameOf = (id) => (id ? Object.values(shop || {}).flat().find((x) => x.id === id)?.name : null);

  return (
    <div className="mt-4 rounded-2xl border border-border bg-card p-4 shadow-sm" data-testid="daily-care">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="font-display text-lg font-bold text-primary">Day {save.day} Care Checklist</h3>
        <button data-testid="open-feed-room" onClick={onOpenShop} className="flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-xs font-semibold text-secondary">
          <ShoppingBag size={13} /> Feed Room
        </button>
      </div>

      <Row label="Grass Hay" value={groom ? "Handled by Groom" : care.hay ? "Complete" : "Not Fed"} done={groom || care.hay} />
      <Row label="Fresh Water" value={groom ? "Handled by Groom" : care.water ? "Complete" : "Not Filled"} done={groom || care.water} />
      <Row label="Grain" value={nameOf(care.grain) || "Optional"} done={!!care.grain} />
      <Row label="Supplement" value={nameOf(care.supplement) || "Optional"} done={!!care.supplement} />
      <Row label="Treat" value={care.treats_given ? `${care.treats_given} given` : "Optional"} done={!!care.treats_given} />
      <Row label="Grooming" value={groom ? "Handled by Groom" : care.groomed ? "Done" : "Optional"} done={groom || care.groomed} />
      <Row label="Health Check" value={care.health_checked ? "Done" : "Optional"} done={care.health_checked} />

      {/* required free actions */}
      {groom ? (
        <div className="mt-3 rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-2.5 text-center" data-testid="groom-handling-basic">
          <p className="text-sm font-bold text-emerald-700">Handled by Groom ✓</p>
          <p className="text-[11px] text-emerald-700">Hay, water &amp; grooming are done automatically. You can still give grain &amp; treats.</p>
        </div>
      ) : (<>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <button data-testid="feed-hay-btn" disabled={busy || care.hay} onClick={() => onCare("hay")}
          className={`flex h-12 items-center justify-center gap-2 rounded-xl font-bold active:scale-95 ${care.hay ? "bg-green-100 text-green-700" : "bg-primary text-primary-foreground"}`}>
          <Wheat size={16} /> {care.hay ? "Hay Fed" : "Feed Grass Hay"}
        </button>
        <button data-testid="fill-water-btn" disabled={busy || care.water} onClick={() => onCare("water")}
          className={`flex h-12 items-center justify-center gap-2 rounded-xl font-bold active:scale-95 ${care.water ? "bg-green-100 text-green-700" : "bg-primary text-primary-foreground"}`}>
          <Droplet size={16} /> {care.water ? "Water Filled" : "Fill Fresh Water"}
        </button>
      </div>
      <p className="mt-1.5 text-center text-[11px] text-muted-foreground">Grass hay &amp; fresh water are always free.</p>
      </>)}

      {!done ? (
        <p data-testid="care-required-msg" className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-center text-sm font-semibold text-amber-800">
          Feed your horse and provide fresh water before beginning today's training.
        </p>
      ) : (
        <div className="mt-3 space-y-2">
          <p data-testid="care-complete-msg" className="rounded-lg bg-green-50 px-3 py-2 text-center text-sm font-semibold text-green-700">
            Basic care complete. Training is now available.
          </p>
          {!care.grain && <Picker testid="grain" label="Optional grain" items={shop?.feeds} inventory={inventory} onGive={(id) => onCare("grain", id)} disabled={busy} />}
          {!care.supplement && <Picker testid="supplement" label="Optional supplement" items={shop?.supplements} inventory={inventory} onGive={(id) => onCare("supplement", id)} disabled={busy} />}
          <Picker testid="treat" label="Give a treat" items={shop?.treats} inventory={inventory} onGive={(id) => onCare("treat", id)} disabled={busy} />
          <div className="flex gap-2">
            <button data-testid="groom-btn" disabled={busy || care.groomed || groom} onClick={() => onCare("groom")}
              className="flex h-10 flex-1 items-center justify-center gap-1 rounded-lg border border-border bg-background text-sm font-semibold text-primary disabled:opacity-50 active:scale-95">
              <Sparkles size={14} /> {groom ? "Groom ✓" : care.groomed ? "Groomed" : "Groom"}
            </button>
            <button data-testid="health-check-btn" disabled={busy || care.health_checked} onClick={() => onCare("health_check")}
              className="flex h-10 flex-1 items-center justify-center gap-1 rounded-lg border border-border bg-background text-sm font-semibold text-primary disabled:opacity-50 active:scale-95">
              <Stethoscope size={14} /> {care.health_checked ? "Checked" : "Health Check"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
